/**
 * Campaign banner module – dismissible promotional bar above the header.
 *
 * Markup contract:
 *   .campaign-banner[data-banner-id="<id>"]
 *     .campaign-banner__text (editor-authored HTML, may contain its own link)
 *     button.campaign-banner__close[aria-label="…"][type="button"]
 *       svg[aria-hidden="true"] > use[href="#close"]
 *
 * The banner is rendered server-side by the CMS only when an editor has
 * enabled it for the current page, so this module only ever has to handle
 * dismissal – there is no "show" path to wire up here.
 *
 * Dismissal is keyed by `data-banner-id` rather than a fixed key, so
 * closing this campaign's banner doesn't suppress a different campaign's
 * banner later, and resets automatically once the active campaign changes.
 *
 * The banner sits above `.site-header`, which is itself `position: sticky`.
 * To keep both stacked correctly while scrolling, this module measures the
 * banner's rendered height and exposes it as `--campaign-banner-height` on
 * the document root; `_header.scss` reads that variable as its `top` offset.
 *
 * @module campaign-banner
 */

const DISMISSED_KEY_PREFIX = 'campaign-banner-dismissed-';
const HEIGHT_CSS_VAR = '--campaign-banner-height';

/**
 * Writes the banner's current rendered height to the document root so the
 * sticky header can offset itself below it.
 *
 * @param {HTMLElement} banner - The `.campaign-banner` element
 * @returns {void}
 */
function syncHeightVariable(banner) {
  document.documentElement.style.setProperty(HEIGHT_CSS_VAR, `${banner.offsetHeight}px`);
}

/**
 * Hides the banner and clears the height offset used by the sticky header.
 *
 * @param {HTMLElement} banner - The `.campaign-banner` element
 * @returns {void}
 */
function hideBanner(banner) {
  banner.hidden = true;
  document.documentElement.style.setProperty(HEIGHT_CSS_VAR, '0px');
}

/**
 * Initialises the `.campaign-banner` element, if present.
 *
 * @returns {void}
 */
function initCampaignBanner() {
  const banner = document.querySelector('.campaign-banner');
  if (!banner) return;

  const bannerId = banner.dataset.bannerId;
  const closeButton = banner.querySelector('.campaign-banner__close');

  // bannerId should always be set by the CMS template; still sync the
  // height offset even without one so the header never ends up stacked
  // behind a stuck-visible banner — only the dismiss key depends on it.
  if (bannerId && sessionStorage.getItem(DISMISSED_KEY_PREFIX + bannerId) === 'true') {
    hideBanner(banner);
    return;
  }

  syncHeightVariable(banner);

  let resizeTicking = false;
  window.addEventListener('resize', () => {
    if (resizeTicking) return;
    resizeTicking = true;
    requestAnimationFrame(() => {
      syncHeightVariable(banner);
      resizeTicking = false;
    });
  }, { passive: true });

  if (!closeButton) return;

  closeButton.addEventListener('click', () => {
    hideBanner(banner);
    if (bannerId) sessionStorage.setItem(DISMISSED_KEY_PREFIX + bannerId, 'true');
  });
}

export { initCampaignBanner };
